import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useSession } from "../../auth/session"
import { useDispatch } from "react-redux"
import { Sku } from "../../api/models"
import Api from "../../api/api"
import { useStoreSku, useOffers, claimRewardThunk, personalOffer } from "../../redux/storeSlice"
import { usePoints } from "../../redux/pointsSlice"
import { useActivePlans, useExtraTrialDays } from "../../redux/plansSlice"
import { FetchableStateStatus } from "../../redux/fetchableState"
import { AppDispatch } from "../../redux/store"
import PointsBalancePill from "../widgets/PointsBalancePill"
import { UpgradeInfo, CrossgradeInfo, formatPrice } from "../widgets/UpgradeInfo"
import SkuImage from "../widgets/SkuImage"
import SkuName from "../widgets/SkuName"
import { useIlokAccounts } from "../../hooks/useIlokAccounts"
import { IlokUpgradeBanner, IlokUpgradeConfirmDialog, useIlokUpgradeConfirmPending } from "../IlokUpgradeBanner"
import { CROSSGRADE_OFFERS_ENABLED, RENT_TO_OWN_ENABLED, REWARD_POINTS_ENABLED, UPGRADE_OFFERS_ENABLED } from "../../featureFlags"

// A bundle upgrade the user is eligible for (owns part of the bundle). Mirrors
// the store's upgrade action, but presented on its own focused card.
// `ilokUnlinked` (the user has no iLok account row at all) gates the one-time
// "link your iLok account first" step on the Upgrade click.
function UpgradeOfferCard({ skuId, ilokUnlinked }: { skuId: string, ilokUnlinked: boolean }) {
  const accessToken = useSession().accessToken ?? ""
  const { data: sku } = useStoreSku(skuId, accessToken)

  if (sku === undefined || sku.upgrade === undefined)
    return <div className="store-card"><span className="spinner" /> Loading…</div>

  return <UpgradeOfferCardContent sku={sku} accessToken={accessToken} ilokUnlinked={ilokUnlinked} />
}

function UpgradeOfferCardContent({ sku, accessToken, ilokUnlinked }: { sku: Sku, accessToken: string, ilokUnlinked: boolean }) {
  const [upgrading, setUpgrading] = useState(false)
  const [ilokConfirmOpen, setIlokConfirmOpen] = useState(false)
  const ilokConfirmPending = useIlokUpgradeConfirmPending()
  const upgrade = sku.upgrade!
  // upgrades are priced against the cheapest one-time offering
  const oneTimeOffering = sku.offerings
    .filter(o => o.paymentType === "one_time")
    .sort((a, b) => a.priceCents - b.priceCents)[0]

  const startCheckout = async () => {
    if (!oneTimeOffering) return
    setUpgrading(true)
    try {
      const { checkout } = await Api.makeSkuCheckout({
        skuId: sku.id,
        offeringId: oneTimeOffering.id,
        redirectUrl: window.location.origin,
        accessToken,
        upgrade,
        promotionId: sku.promotion?.id ?? null,
      })
      window.location.href = checkout.attributes.url
    } catch (err) {
      console.error("Upgrade checkout failed:", err)
      setUpgrading(false)
    }
  }

  // unlinked iLok account and the reminder not yet shown → interrupt once;
  // an unknown iLok state (still loading, request failed) never blocks
  const handleUpgrade = () => {
    if (ilokUnlinked && ilokConfirmPending)
      setIlokConfirmOpen(true)
    else
      startCheckout()
  }

  return (
    <div className="store-card">
      {ilokConfirmOpen && (
        <IlokUpgradeConfirmDialog onContinue={startCheckout} onClose={() => setIlokConfirmOpen(false)} />
      )}
      <SkuImage sku={sku} alt={sku.name} />
      <div className="store-card-body">
        <div className="store-card-name"><SkuName sku={sku} variant="section" /></div>
        <p className="store-card-desc">{sku.description}</p>
        {sku.highlights.length > 0 && (
          <ul className="tier-features">
            {sku.highlights.map((highlight) => (
              <li key={highlight}>{highlight}</li>
            ))}
          </ul>
        )}
        <div className="store-card-footer">
          <div className="store-upgrade-info">
            <span className="store-price">{formatPrice(upgrade.priceCents / 100)}</span>
            <UpgradeInfo sku={sku} accessToken={accessToken} />
          </div>
          <button className="btn-buy" onClick={handleUpgrade} disabled={upgrading}>
            {upgrading ? "Loading…" : "Upgrade"}
          </button>
        </div>
      </div>
    </div>
  )
}

// A crossgrade the user is eligible for (owns configured outside products that
// discount the sku's one-time price). Like the upgrade card, but nothing is
// traded in — the owned products stay; the server re-verifies the echoed price.
// No iLok-link interruption here: that copy is about the bundle's own plug-ins.
function CrossgradeOfferCard({ skuId }: { skuId: string }) {
  const accessToken = useSession().accessToken ?? ""
  const { data: sku } = useStoreSku(skuId, accessToken)

  if (sku === undefined || sku.crossgrade === undefined)
    return <div className="store-card"><span className="spinner" /> Loading…</div>

  return <CrossgradeOfferCardContent sku={sku} accessToken={accessToken} />
}

function CrossgradeOfferCardContent({ sku, accessToken }: { sku: Sku, accessToken: string }) {
  const [buying, setBuying] = useState(false)
  const crossgrade = sku.crossgrade!
  // crossgrades price the sku's one-time offering (a singleton per sku)
  const oneTimeOffering = sku.offerings
    .filter(o => o.paymentType === "one_time")
    .sort((a, b) => a.priceCents - b.priceCents)[0]

  const startCheckout = async () => {
    if (!oneTimeOffering) return
    setBuying(true)
    try {
      const { checkout } = await Api.makeSkuCheckout({
        skuId: sku.id,
        offeringId: oneTimeOffering.id,
        redirectUrl: window.location.origin,
        accessToken,
        crossgrade: { priceCents: crossgrade.priceCents },
        promotionId: sku.promotion?.id ?? null,
      })
      window.location.href = checkout.attributes.url
    } catch (err) {
      console.error("Crossgrade checkout failed:", err)
      setBuying(false)
    }
  }

  return (
    <div className="store-card">
      <SkuImage sku={sku} alt={sku.name} />
      <div className="store-card-body">
        <div className="store-card-name"><SkuName sku={sku} variant="section" /></div>
        <p className="store-card-desc">{sku.description}</p>
        {sku.highlights.length > 0 && (
          <ul className="tier-features">
            {sku.highlights.map((highlight) => (
              <li key={highlight}>{highlight}</li>
            ))}
          </ul>
        )}
        <div className="store-card-footer">
          <div className="store-upgrade-info">
            <span className="store-price">{formatPrice(crossgrade.priceCents / 100)}</span>
            <span className="store-price-was">{formatPrice(crossgrade.listPriceCents / 100)}</span>
            <CrossgradeInfo sku={sku} accessToken={accessToken} />
          </div>
          <button className="btn-buy" onClick={startCheckout} disabled={buying}>
            {buying ? "Loading…" : "Buy"}
          </button>
        </div>
      </div>
    </div>
  )
}

// Store skus claimable at no cost: they carry a free offering, or the user's
// crossgrade price on the one-time offering reached 0 (claimed the same way —
// the server recomputes and grants without a checkout). Mirrors the store's
// claim action on its own focused card.
function FreeOfferCard({ skuId }: { skuId: string }) {
  const accessToken = useSession().accessToken ?? ""
  const { data: sku } = useStoreSku(skuId, accessToken)

  if (sku === undefined)
    return <div className="store-card"><span className="spinner" /> Loading…</div>

  return <FreeOfferCardContent sku={sku} accessToken={accessToken} />
}

function FreeOfferCardContent({ sku, accessToken }: { sku: Sku, accessToken: string }) {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const { refresh: refreshPoints } = usePoints(accessToken)
  const [claiming, setClaiming] = useState(false)

  const offer = personalOffer(sku)
  const freeOffering = sku.offerings.find(o => o.paymentType === "free")
    ?? (offer?.kind === "crossgrade" && offer.priceCents === 0
      ? sku.offerings.find(o => o.paymentType === "one_time")
      : undefined)

  const handleClaim = async () => {
    if (!freeOffering) return
    setClaiming(true)
    try {
      const { pointSpending } = await dispatch(claimRewardThunk({ skuId: sku.id, offeringId: freeOffering.id, accessToken })).unwrap()
      refreshPoints()  // the claim re-derives the offer list, dropping this card
      navigate(`/purchase/success?skuId=${sku.id}&rewardClaimId=${pointSpending.id}`)
      return
    } catch (err) {
      console.error("Claim failed:", err)
    }
    setClaiming(false)
  }

  return (
    <div className="store-card">
      <SkuImage sku={sku} alt={sku.name} />
      <div className="store-card-body">
        <div className="store-card-name"><SkuName sku={sku} variant="section" /></div>
        <p className="store-card-desc">{sku.description}</p>
        {sku.highlights.length > 0 && (
          <ul className="tier-features">
            {sku.highlights.map((highlight) => (
              <li key={highlight}>{highlight}</li>
            ))}
          </ul>
        )}
        <div className="store-card-footer">
          <div className="store-upgrade-info">
            <span className="store-points-cost">Free</span>
            {sku.crossgrade !== undefined && freeOffering?.paymentType === "one_time" && (
              <CrossgradeInfo sku={sku} accessToken={accessToken} />
            )}
          </div>
          <button className="btn-claim" onClick={handleClaim} disabled={freeOffering === undefined || claiming}>
            {claiming ? "Claiming…" : "Claim"}
          </button>
        </div>
      </div>
    </div>
  )
}

// Store skus the user can claim with reward points (they currently have
// enough). The cheapest reward offering prices the claim in points.
function RewardOfferCard({ skuId }: { skuId: string }) {
  const accessToken = useSession().accessToken ?? ""
  const { data: sku } = useStoreSku(skuId, accessToken)

  if (sku === undefined)
    return <div className="store-card"><span className="spinner" /> Loading…</div>

  return <RewardOfferCardContent sku={sku} accessToken={accessToken} />
}

function RewardOfferCardContent({ sku, accessToken }: { sku: Sku, accessToken: string }) {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const { data: points, refresh: refreshPoints } = usePoints(accessToken)
  const [claiming, setClaiming] = useState(false)

  const rewardOffering = sku.offerings
    .filter(o => o.paymentType === "reward")
    .sort((a, b) => a.priceCents - b.priceCents)[0]
  const pointsCost = rewardOffering?.priceCents ?? 0
  const balance = points ? points.topups - points.spendings : 0
  const canClaim = rewardOffering !== undefined && !!points && balance >= pointsCost

  const handleClaim = async () => {
    if (!rewardOffering) return
    setClaiming(true)
    try {
      const { pointSpending } = await dispatch(claimRewardThunk({ skuId: sku.id, offeringId: rewardOffering.id, accessToken })).unwrap()
      refreshPoints()  // updated balance re-derives the offer list, dropping this card
      navigate(`/purchase/success?skuId=${sku.id}&rewardClaimId=${pointSpending.id}`)
      return
    } catch (err) {
      console.error("Claim failed:", err)
    }
    setClaiming(false)
  }

  return (
    <div className="store-card">
      <SkuImage sku={sku} alt={sku.name} />
      <div className="store-card-body">
        <div className="store-card-name"><SkuName sku={sku} variant="section" /></div>
        <p className="store-card-desc">{sku.description}</p>
        {sku.highlights.length > 0 && (
          <ul className="tier-features">
            {sku.highlights.map((highlight) => (
              <li key={highlight}>{highlight}</li>
            ))}
          </ul>
        )}
        <div className="store-card-footer">
          <span className="store-points-cost">{pointsCost} pts</span>
          <button className="btn-claim" onClick={handleClaim} disabled={!canClaim || claiming}>
            {claiming ? "Claiming…" : "Claim"}
          </button>
        </div>
      </div>
    </div>
  )
}

// Subscription offer surfaced only when the user has unlocked extra free-trial
// days (which the backend grants for owned products, and only while they have no
// subscription). Links through to the subscription page where the extra days
// apply to any plan.
function SubscriptionOfferCard({ extraDays }: { extraDays: number }) {
  const dayLabel = extraDays === 1 ? "day" : "days"
  return (
    <Link to="/plan" className="store-card subscription-offer-card">
      <div className="subscription-offer-banner" aria-hidden="true">&#127873;</div>
      <div className="store-card-body">
        <div className="store-card-name">Subscribe with bonus trial days</div>
        <p className="store-card-desc">
          You've unlocked <strong>{extraDays} extra {dayLabel}</strong> of free trial — added on top of every plan's standard trial for the products you own. Try any plan risk-free.
        </p>
        <div className="store-card-footer">
          <span className="subscription-offer-badge">+{extraDays} {dayLabel} free</span>
          <span className="btn-buy">View plans</span>
        </div>
      </div>
    </Link>
  )
}

// The empty-state explanation lists only the offer kinds this build shows
// (upgrades and point rewards are FCS-hidden, see featureFlags.ts).
function emptyStateHint(): string {
  const parts: string[] = []
  if (UPGRADE_OFFERS_ENABLED)
    parts.push("upgrade deals appear once you own part of a bundle")
  if (CROSSGRADE_OFFERS_ENABLED)
    parts.push("crossgrade deals appear when products you own earn a discount")
  parts.push("free products appear when one is available for you")
  if (REWARD_POINTS_ENABLED)
    parts.push("rewards appear when your point balance is high enough to claim them")
  const sentence = parts.length === 1
    ? parts[0]
    : `${parts.slice(0, -1).join(", ")}${parts.length > 2 ? "," : ""} and ${parts[parts.length - 1]}`
  return sentence.charAt(0).toUpperCase() + sentence.slice(1) + "."
}

export default function OffersPage() {
  const accessToken = useSession().accessToken ?? ""
  const { loading, upgrades, crossgrades, free, rewards, total } = useOffers(accessToken)
  const { data: points } = usePoints(accessToken)
  const pointsBalance = points ? points.topups - points.spendings : undefined
  // Trigger the active-plans fetch so extraTrialDays is populated on this page.
  const { status: plansStatus } = useActivePlans(accessToken)
  const extraTrialDays = useExtraTrialDays()
  const extraDays = extraTrialDays.reduce((sum, e) => sum + e.days, 0)
  const plansLoading = plansStatus === FetchableStateStatus.Initializing
  // the bonus-trial offer starts a rent-to-own plan, so it goes with that feature
  const hasTrialOffer = RENT_TO_OWN_ENABLED && extraDays > 0
  // the iLok reminders fire only on a POSITIVE "no account linked" answer
  const { accounts: ilokAccounts } = useIlokAccounts(accessToken)
  const ilokUnlinked = ilokAccounts !== undefined && ilokAccounts.length === 0

  const anyLoading = loading || plansLoading

  return (
    <div className="page">
      <div className="dashboard-header">
        <h1 className="page-title" style={{ margin: 0 }}>Your Offers</h1>
        <PointsBalancePill />
      </div>

      {anyLoading && (
        <p className="empty-state"><span className="spinner" /> Finding offers for you…</p>
      )}

      {!anyLoading && total === 0 && !hasTrialOffer && (
        <div className="empty-state-block">
          <p>No personalized offers right now.</p>
          <p className="empty-state-hint">{emptyStateHint()}</p>
        </div>
      )}

      {!plansLoading && hasTrialOffer && (
        <section className="offers-section">
          <h2 className="offers-section-title">Start a subscription</h2>
          <p className="offers-section-subtitle">You've unlocked extra free-trial days on any plan — thanks to the products you already own.</p>
          <div className="store-grid">
            <SubscriptionOfferCard extraDays={extraDays} />
          </div>
        </section>
      )}

      {!loading && upgrades.length > 0 && (
        <section className="offers-section">
          <h2 className="offers-section-title">Upgrade your bundles</h2>
          <p className="offers-section-subtitle">You already own part of these bundles — complete them at a discount.</p>
          {ilokUnlinked && <IlokUpgradeBanner />}
          <div className="store-grid">
            {upgrades.map((id) => (
              <UpgradeOfferCard key={id} skuId={id} ilokUnlinked={ilokUnlinked} />
            ))}
          </div>
        </section>
      )}

      {!loading && crossgrades.length > 0 && (
        <section className="offers-section">
          <h2 className="offers-section-title">Crossgrades</h2>
          <p className="offers-section-subtitle">Products you own earn you a discount on these — and you keep what you have.</p>
          <div className="store-grid">
            {crossgrades.map((id) => (
              <CrossgradeOfferCard key={id} skuId={id} />
            ))}
          </div>
        </section>
      )}

      {!loading && free.length > 0 && (
        <section className="offers-section">
          <h2 className="offers-section-title">Free products</h2>
          <p className="offers-section-subtitle">Yours to claim at no cost.</p>
          <div className="store-grid">
            {free.map((id) => (
              <FreeOfferCard key={id} skuId={id} />
            ))}
          </div>
        </section>
      )}

      {!loading && rewards.length > 0 && (
        <section className="offers-section">
          <h2 className="offers-section-title">Redeem with points</h2>
          <p className="offers-section-subtitle">You have enough reward points{pointsBalance !== undefined ? <> (<span style={{ color: "var(--nfa-blue)", fontWeight: "bold" }}>{pointsBalance}</span>)</> : ""} to claim these for free.</p>
          <div className="store-grid">
            {rewards.map((id) => (
              <RewardOfferCard key={id} skuId={id} />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
